import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileCheck, MessageSquareHeart, Send, CheckCircle2, ArrowRight, Home, ChevronRight } from 'lucide-react';
import { useContactForm } from '../../hooks/useContactForm';
import { toast } from 'react-toastify';
import { useCmsContent } from '../../hooks/useCmsContent';

const HeroSection = () => {
  const { submitContactForm, loading } = useContactForm();
  const { getContentValue, loading: contentLoading } = useCmsContent({ section: 'counters' });
  const [formStep, setFormStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    propertyType: '',
    size: '',
    rooms: '',
    bathrooms: '',
    year: '',
    condition: '',
    urgency: '',
    additionalFeatures: [] as string[],
    message: ''
  });

  const propertyTypes = [
    'Appartamento',
    'Villa',
    'Casa Indipendente',
    'Attico',
    'Terreno',
    'Commerciale',
    'Garage/Box Auto'
  ];

  const conditions = [
    'Nuovo/Ristrutturato',
    'Buono Stato',
    'Da Ristrutturare',
    'Da Ristrutturare Completamente'
  ];

  const urgencyOptions = [
    'Immediata (1-3 mesi)',
    'Breve termine (3-6 mesi)',
    'Medio termine (6-12 mesi)',
    'Lungo termine (>12 mesi)'
  ];

  const features = [
    'Terrazzo',
    'Balcone',
    'Giardino',
    'Garage',
    'Posto Auto',
    'Cantina',
    'Ascensore',
    'Aria Condizionata',
    'Riscaldamento Autonomo'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formStep < 3) {
      setFormStep(formStep + 1);
      return;
    }
    
    // Invia i dati
    setIsSubmitting(true);
    
    try {
      // Prepara i dati del messaggio in formato leggibile
      const formattedMessage = `
Indirizzo: ${form.address || 'Non specificato'}
Tipologia: ${form.propertyType || 'Non specificato'}
Superficie: ${form.size || 'Non specificato'} m²
Locali: ${form.rooms || 'Non specificato'}
Bagni: ${form.bathrooms || 'Non specificato'}
Anno costruzione: ${form.year || 'Non specificato'}
Condizioni: ${form.condition || 'Non specificato'}
Tempistica: ${form.urgency || 'Non specificato'}
Dotazioni: ${form.additionalFeatures.join(', ') || 'Nessuna specificata'}

Note aggiuntive:
${form.message || 'Nessuna'}
      `;

      const { success, error } = await submitContactForm({
        name: `${form.name} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        message: formattedMessage,
        type: 'acquisition'
      }, {
        title: "Richiesta di valutazione immobile"
      });

      if (success) {
        setSubmitted(true);
        toast.success("La tua richiesta è stata inviata con successo!");
      } else {
        toast.error(`Errore nell'invio della richiesta: ${error}`);
      }
    } catch (error) {
      toast.error("Si è verificato un errore. Riprova più tardi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (feature: string) => {
    setForm(prev => {
      const currentFeatures = prev.additionalFeatures;
      if (currentFeatures.includes(feature)) {
        return {
          ...prev,
          additionalFeatures: currentFeatures.filter(f => f !== feature)
        };
      } else {
        return {
          ...prev,
          additionalFeatures: [...currentFeatures, feature]
        };
      }
    });
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {[0, 1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center space-y-2 w-16">
            <div 
              className={`flex items-center justify-center w-12 h-12 rounded-full font-semibold transition-all ${
                step === formStep 
                  ? 'bg-secondary text-white scale-110 shadow-lg' 
                  : step < formStep 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step < formStep ? (
                <CheckCircle2 className="h-6 w-6" />
              ) : (
                step + 1
              )}
            </div>
            <span className={`text-xs font-medium text-center transition-colors ${
              step <= formStep ? 'text-primary' : 'text-gray-400'
            }`}>
              {step === 0 ? 'Dati' : 
               step === 1 ? 'Immobile' : 
               step === 2 ? 'Caratteristiche' : 
               'Note'}
            </span>
          </div>
          {step < 3 && (
            <div 
              className={`flex-grow h-1 mx-1 rounded-full transition-all ${
                step < formStep ? 'bg-primary' : 'bg-gray-200'
              }`}
            ></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderFormStep = () => {
    switch(formStep) {
      case 0:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-primary mb-4">Iniziamo con i tuoi dati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Nome<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Il tuo nome"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Cognome<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Il tuo cognome"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Email<span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="La tua email"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Telefono<span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Il tuo numero di telefono"
                />
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <p className="text-blue-700 text-sm">
                I tuoi dati saranno utilizzati esclusivamente per contattarti riguardo alla valutazione del tuo immobile.
              </p>
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-primary mb-4">Dettagli dell'immobile</h3>
            
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Indirizzo completo dell'immobile<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                required
                placeholder="Via, numero civico, città, provincia"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Tipologia<span className="text-red-500">*</span>
                </label>
                <select
                  name="propertyType"
                  value={form.propertyType}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                >
                  <option value="">Seleziona tipologia...</option>
                  {propertyTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Superficie (m²)<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="size"
                  value={form.size}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Es. 100"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Locali<span className="text-red-500">*</span>
                </label>
                <select
                  name="rooms"
                  value={form.rooms}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                >
                  <option value="">Seleziona...</option>
                  {[1, 2, 3, 4, 5, '6+'].map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Bagni
                </label>
                <select
                  name="bathrooms"
                  value={form.bathrooms}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                >
                  <option value="">Seleziona...</option>
                  {[1, 2, 3, 4, '5+'].map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Anno di costruzione
                </label>
                <input
                  type="number"
                  name="year"
                  value={form.year}
                  onChange={handleChange}
                  min="1900"
                  max={new Date().getFullYear()}
                  placeholder="Es. 1995"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                />
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-primary mb-4">Caratteristiche aggiuntive</h3>
            
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Stato dell'immobile<span className="text-red-500">*</span>
              </label>
              <select
                name="condition"
                value={form.condition}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              >
                <option value="">Seleziona stato...</option>
                {conditions.map((condition, index) => (
                  <option key={index} value={condition}>{condition}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tempistica di vendita desiderata
              </label>
              <select
                name="urgency"
                value={form.urgency}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              >
                <option value="">Seleziona tempistica...</option>
                {urgencyOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caratteristiche presenti
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6 bg-gray-50 p-4 rounded-lg">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`feature-${index}`}
                      checked={form.additionalFeatures.includes(feature)}
                      onChange={() => handleFeatureChange(feature)}
                      className="h-4 w-4 text-secondary border-gray-300 rounded focus:ring-secondary"
                    />
                    <label htmlFor={`feature-${index}`} className="ml-2 text-sm text-gray-700">
                      {feature}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-primary mb-4">Note aggiuntive</h3>
            
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Altre informazioni o richieste particolari
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
                placeholder="Condividi con noi qualsiasi altra informazione che ritieni utile..."
              ></textarea>
            </div>
            
            <div className="bg-primary/5 p-5 rounded-lg border-l-4 border-primary">
              <h4 className="font-semibold text-primary mb-2 flex items-center">
                <CheckCircle2 className="h-5 w-5 mr-2 text-secondary" />
                Prima di inviare
              </h4>
              <p className="text-gray-600">
                Inviando questo modulo, un nostro esperto ti contatterà per fissare un appuntamento e
                procedere con la valutazione gratuita del tuo immobile. Tutti i dati saranno trattati in
                conformità con la nostra <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
              
              <div className="mt-4 flex items-start">
                <input 
                  type="checkbox" 
                  id="privacy-consent" 
                  required
                  className="h-5 w-5 text-secondary border-gray-300 rounded mt-1" 
                />
                <label htmlFor="privacy-consent" className="ml-2 text-sm text-gray-600">
                  Autorizzo il trattamento dei miei dati personali ai sensi del D.Lgs. 196/2003 e del GDPR (Regolamento UE 2016/679) <span className="text-red-500">*</span>
                </label>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <section className="relative pt-20 pb-32 bg-primary overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80" 
          alt="Real estate background" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary/90"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <span className="inline-block bg-secondary/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Servizio esclusivo
            </span>
            <h1 className="text-5xl font-monomakh mb-6 leading-tight">Valorizza e Vendi<br />il Tuo Immobile</h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Con la nostra esperienza decennale nel mercato immobiliare trasformiamo il processo di vendita in un percorso semplice, trasparente e vantaggioso.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-lg hover:bg-white/15 transition-all"
              >
                <div className="text-secondary text-3xl font-bold mb-1">{getContentValue('percentuale_clienti_soddisfatti') || "97%"}</div>
                <p className="text-gray-200">Degli immobili venduti entro 3 mesi</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-lg hover:bg-white/15 transition-all"
              >
                <div className="text-secondary text-3xl font-bold mb-1">+{getContentValue('immobili_venduti') || "300"}</div>
                <p className="text-gray-200">Proprietari soddisfatti dal 2017</p>
              </motion.div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#valutazione" 
                className="bg-secondary hover:bg-secondary-light text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center"
              >
                <FileCheck className="h-5 w-5 mr-2" />
                Richiedi valutazione gratuita
              </a>
              <a 
                href="#come-funziona" 
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center"
              >
                <MessageSquareHeart className="h-5 w-5 mr-2" />
                Parlaci del tuo immobile
              </a>
            </div>
          </motion.div>
          
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-10 rounded-xl shadow-2xl"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold text-primary mb-4">Richiesta Inviata con Successo!</h3>
                <p className="text-gray-600 mb-6">
                  Grazie per averci contattato. Un nostro consulente immobiliare ti ricontatterà entro 24 ore
                  per organizzare una valutazione gratuita del tuo immobile.
                </p>
                <div className="p-6 bg-gray-50 rounded-lg text-left shadow-inner">
                  <p className="font-medium text-gray-700 mb-3">Nel frattempo, puoi:</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-600 hover:text-primary transition-colors">
                      <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0" />
                      <a href="/immobili" className="ml-2 hover:underline">
                        Esplorare i nostri immobili in vendita
                      </a>
                    </li>
                    <li className="flex items-center text-gray-600 hover:text-primary transition-colors">
                      <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0" />
                      <a href="#testimonials" className="ml-2 hover:underline">
                        Leggere i feedback dei nostri clienti
                      </a>
                    </li>
                    <li className="flex items-center text-gray-600 hover:text-primary transition-colors">
                      <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0" />
                      <a href="/chi-siamo" className="ml-2 hover:underline">
                        Scoprire di più sulla nostra agenzia
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              id="valutazione"
              className="bg-white p-10 rounded-xl shadow-2xl"
            >
              <form onSubmit={handleSubmit}>
                {/* Step indicators */}
                {renderStepIndicator()}
                
                {/* Form steps */}
                <motion.div 
                  key={formStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="min-h-[380px]"
                >
                  {renderFormStep()}
                </motion.div>
                
                {/* Form navigation */}
                <div className="mt-10 flex justify-between">
                  {formStep > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormStep(formStep - 1)}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center"
                    >
                      <ArrowRight className="h-5 w-5 mr-2 rotate-180" />
                      Indietro
                    </button>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loading || isSubmitting}
                    className={`ml-auto px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-light transition-colors font-semibold flex items-center shadow-md ${
                      loading || isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading || isSubmitting ? (
                      <>
                        <div className="animate-spin h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                        Invio in corso...
                      </>
                    ) : formStep < 3 ? (
                      <>
                        Continua
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    ) : (
                      <>
                        Invia Richiesta
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;